import React, { useEffect, useState } from 'react';
import { WebUrl } from './WebUrl';
import { toast } from 'react-toastify';
import { DateField } from './DatePicker';
import { useForm } from 'react-hook-form';
import { InputField } from './InputField';
import { FieldArray } from './FieldArray';
import { SelectField } from './SelectField';
import { SectionField } from './SectionField';
import { CheckboxField } from './CheckboxField';
import { SpanTextInput } from './SpanTextInput';
import { CheckMarkField } from './CheckMarkField';
import { FileUploadField } from './FileUploadField';
import { RadioButtonField } from './RadioButtonField';
import { SearchSelectField } from './SearchSelectField';
import { Alert, Button, FormControl, Slide, Snackbar } from '@mui/material';

const FieldComponent = {
  weburl: WebUrl,
  date: DateField,
  text: InputField,
  select: SelectField,
  section: SectionField,
  spantext: SpanTextInput,
  upload: FileUploadField,
  radio: RadioButtonField,
  multiweburl: FieldArray,
  checkbox: CheckboxField,
  checkmark: CheckMarkField,
  search: SearchSelectField,
  'radio-options': RadioButtonField,
};

export const CreateField = ({
  fields,
  apiCall,
  formControl,
  setShowSnack,
  parentId = null,
  isFirstLevel = true,
}) => {
  const [selectedValues, setSelectedValues] = useState(() => {
    return fields.reduce((acc, field) => {
      if (
        field.type === 'radio' ||
        field.type === 'radio-options' ||
        field.type === 'checkbox' ||
        field.type === 'checkmark'
      ) {
        const defaultOption = field?.child?.find((child) => child?.IsSelected)?.label || '';
        acc[field.id] = defaultOption;

        const selectedChild = field?.child?.find((child) => child?.label === defaultOption);
        if (selectedChild?.child?.length) {
          selectedChild?.child?.forEach((subChild) => {
            if (
              subChild?.type === 'radio' ||
              subChild?.type === 'radio-options' ||
              subChild?.type === 'checkbox' ||
              subChild?.type === 'checkmark'
            ) {
              const subDefault = subChild?.child?.find((c) => c?.IsSelected)?.label || '';
              acc[subChild.id] = subDefault;
            }
          });
        }
      }
      return acc;
    }, {});
  });

  const handleSelectionChange = (fieldId, selectedValue) => {
    setSelectedValues((prev) => {
      const updatedValues = { ...prev, [fieldId]: selectedValue };
      const selectedField = fields.find((field) => field.id === fieldId);

      if (selectedField?.child?.length) {
        selectedField.child.forEach((child) => {
          if (child?.label === selectedValue) {
            if (child?.type === 'section') {
              // Set the section ID with the selected value
              updatedValues[child.id] = selectedValue;
              // Add key-value pair to formControl if it doesn't exist
              if (!formControl.getValues()[child.id]) {
                formControl.setValue(child.id, selectedValue);
              }
            }
          } else {
            // Remove previous section value
            delete updatedValues[child.id];
            formControl.unregister(child.id);

            if (child?.child) {
              child?.child?.forEach((subChild) => {
                delete updatedValues[subChild.id];
                formControl.unregister(subChild.id);
              });
            }
          }
        });
      }

      return updatedValues;
    });
  };

  const getFilteredChildren = (field, isFirstLevelCheck) => {
    if (field?.child?.length > 0) {
      if (field.value) {
        return field?.child
          ?.filter((child) => {
            return child.label === (selectedValues[field.id] || field?.value);
          })
          ?.map((child) => ({
            ...child,
            child: getFilteredChildren(child, false),
          }));
      }
    } else {
      return [
        {
          ...field,
          child: undefined,
        },
      ];
    }

    return field?.child
      ?.filter((child) => {
        if (typeof child?.IsVisible === 'undefined' && typeof child?.parentid === 'undefined') {
          return true;
        }
        return (
          child?.IsVisible &&
          child?.parentid === field?.id &&
          FieldComponent[child?.type?.toLowerCase()]
        );
      })
      ?.filter(
        (child) =>
          !isFirstLevelCheck ||
          selectedValues[field.id] === true ||
          child.label === selectedValues[field.id],
      )
      ?.map((child) => ({
        ...child,
        child: getFilteredChildren(child, false),
      }));
  };

  return fields
    ?.filter((item) => {
      if (typeof item?.IsVisible === 'undefined' && typeof item?.parentid === 'undefined') {
        return true;
      }
      if (typeof item?.IsVisible !== 'undefined' && !item?.IsVisible) {
        return false;
      }
      if (parentId !== null) {
        return item?.parentid === parentId && FieldComponent[item?.type?.toLowerCase()];
      }
      return FieldComponent[item?.type?.toLowerCase()];
    })
    ?.map((field, i) => {
      const Component = FieldComponent[field?.type?.toLowerCase()];
      if (!Component || !field?.id) return null;

      // Don't process children here if type is 'section'
      if (field?.type?.toLowerCase() === 'section') {
        return (
          <div key={field?.id + i} className="mt-3 rounded-lg border p-4 first:mt-0">
            <Component {...{ field, apiCall, formControl, setShowSnack, selectedValues }} />
          </div>
        );
      }

      const filteredChildren = getFilteredChildren(field, isFirstLevel);
      const hasChildren = filteredChildren?.length > 0;

      return (
        <div
          key={field?.id + i}
          className={`${!['text', 'select']?.includes(field?.type?.toLowerCase()) && 'rounded-lg border p-4'} ${hasChildren ? 'mt-3' : 'mt-3 first:mt-0'}`}
        >
          <div>
            {field?.prefix && <span className="text-base text-black/60">{field?.prefix}</span>}
            <Component
              {...{ field, apiCall, formControl, setShowSnack, selectedValues }}
              onSelectionChange={handleSelectionChange}
            />
            {field?.suffix && <span className="text-base text-black/60">{field?.suffix}</span>}
          </div>

          {hasChildren && (
            <div className="rounded-lg bg-gray-50">
              <CreateField
                fields={filteredChildren}
                apiCall={apiCall}
                formControl={formControl}
                setShowSnack={setShowSnack}
                parentId={field?.id}
                isFirstLevel={true}
              />
            </div>
          )}
        </div>
      );
    });
};

const DynamicForm = ({ fields, apiCall, onSubmit }) => {
  const formControl = useForm();
  const {
    handleSubmit,
    formState: { errors },
    setFocus,
  } = formControl;

  const [showSnack, setShowSnack] = useState({});

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      setFocus(firstErrorKey);
    }
  }, [errors, setFocus]);

  const submitAndReset = (data) => {
    onSubmit(data)
      .then(() => {
        formControl.reset();
      })
      .catch((error) => {
        toast.error(`Error: ${error.message}`);
      });
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <div className="mb-5 flex h-full flex-col items-center justify-center">
        <form className="!w-[600px] max-w-xl" onSubmit={handleSubmit(submitAndReset)}>
          <FormControl fullWidth>
            <Snackbar
              autoHideDuration={5000}
              open={!!showSnack.message}
              onClose={() => setShowSnack({})}
              anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
              TransitionComponent={(props) => <Slide {...props} direction="left" />}
            >
              <Alert
                sx={{ width: '100%' }}
                severity={showSnack.type}
                onClose={() => setShowSnack({})}
              >
                {showSnack.message}
              </Alert>
            </Snackbar>
            {Array.isArray(fields) && (
              <CreateField
                fields={fields}
                apiCall={apiCall}
                formControl={formControl}
                setShowSnack={setShowSnack}
              />
            )}
            {!!fields?.length && (
              <Button style={{ marginTop: 10, marginBottom: 10 }} variant="contained" type="submit">
                Submit
              </Button>
            )}
            {Object.keys(errors)?.length > 0 && (
              <div className="mt-5 border border-red-400 bg-red-50 p-3">
                <p className="text-base font-bold text-red-700">Please fix the following errors:</p>
                <ul className="pl-7">
                  {Object.keys(errors)?.map((key) => (
                    <li className="list-disc marker:text-red-600" key={key}>
                      <p className="text-sm font-medium text-red-700">
                        {errors[key]?.message || `Field ${key} is invalid`}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </FormControl>
        </form>
        <div id="webUrlLoad" />
      </div>
    </div>
  );
};

export default DynamicForm;
